import React from "react";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import useFieldName from "./use-field-name";
import useFieldValue from "./use-field-value";
import useFieldId from "./use-field-id";
import useFieldOptions from "./use-field-options";
import FieldWrapper from "../layouts/FieldWrapper";
import FieldContext from "../contexts/FieldContext";

export default function useField(props = null) {

    const name = useFieldName(props.name);
    const [value, setValue] = useFieldValue(props.fromName || name, props.defaultValue);
    const id = useFieldId();


    const debouncedOnChange = debounce((value) => {
        if (typeof props.onChange === 'function') {
            props.onChange(value);
        }
    }, 200);

    const onChange = (value) => {
        setValue(value);
        debouncedOnChange(value);
    };

    const onBlur = () => {
        if (typeof props.onBlur === 'function') {
            props.onBlur(value);
        }
    };

    const onFocus = () => {
        if (typeof props.onFocus === 'function') {
            props.onFocus(value);
        }
    };

    const field = {
        ...props,
        id,
        name,
        value,
        onChange,
        onBlur,
        onFocus,
        options: useFieldOptions(props),
    };

    const wrap = (renderCallback, naked = false) => {

        if (naked === true || props.naked === true) {
            return (
                <FieldContext.Provider value={field}>
                    {renderCallback({className: props.className})}
                </FieldContext.Provider>
            );
        }

        return (
            <FieldContext.Provider value={field}>
                <FieldWrapper id={id} name={name} label={props.label} required={props.required} className={props.className}>
                    {props.beforeField}
                    {(props.description !== null) ? (<p className={'field--description'}>{props.description}</p>) : null}
                    {renderCallback()}
                </FieldWrapper>
            </FieldContext.Provider>
        )
    }

    field.wrap = wrap;

    return field;
}

useField.defaultProps = {
    ...useFieldOptions.defaultProps,
    beforeField: null,
    defaultValue: null,
    description: null,
    className: "",
    label: null,
    naked: false,
    required: false,
    onChange: null,
    onBlur: null,
    onFocus: null,
};

useField.propTypes = {
    ...useFieldOptions.propTypes,
    beforeField: PropTypes.node,
    defaultValue: PropTypes.any,
    description: PropTypes.node,
    className: PropTypes.string,
    label: PropTypes.node,
    naked: PropTypes.bool,
    name: PropTypes.string,
    fromName: PropTypes.string,
    required: PropTypes.bool,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
};
